<template>
  <div class="list-page-container">
    <div class="control-panel">
      <div class="control-container">
        <div class="header-info">
          <i class="el-icon-star-off"></i>
          <span>管理用户等级体系，包括等级配置、经验值阈值及等级特权设置</span>
        </div>
        <div class="action-wrapper">
          <el-button
            v-if="hasPermission('sys:level:save')"
            type="primary"
            icon="el-icon-plus"
            @click="handleAdd"
          >
            新增等级
          </el-button>
        </div>
      </div>
    </div>

    <!-- 统计概览 -->
    <div class="control-panel has-query" v-if="statistics" style="padding: 12px 20px;">
      <div class="stats-row">
        <div class="stat-card">
          <div class="stat-value">{{ statistics.totalLevels || 0 }}</div>
          <div class="stat-label">总等级数</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ statistics.activeLevels || 0 }}</div>
          <div class="stat-label">启用等级</div>
        </div>
        <div class="stat-card" v-for="item in (statistics.levelDistribution || []).slice(0, 5)" :key="item.levelCode">
          <div class="stat-value">{{ item.userCount || 0 }}</div>
          <div class="stat-label">{{ item.levelName }}</div>
        </div>
      </div>
    </div>

    <el-card class="list-card">
      <el-table
        ref="table"
        v-loading="loading"
        :data="tableData"
        class="custom-table"
        height="100%"
        style="width: 100%"
      >
        <el-table-column prop="levelCode" label="等级标识" width="100" align="center">
          <template slot-scope="scope">
            <el-tag size="small" effect="dark" :color="scope.row.levelColor || '#909399'" style="color: #fff; border: none;">{{ scope.row.levelCode }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="levelName" label="等级名称" width="130"></el-table-column>
        <el-table-column prop="experienceThreshold" label="所需经验值" width="150" align="center">
          <template slot-scope="scope">
            <span style="font-weight: 600; color: #e6a23c;">{{ formatNumber(scope.row.experienceThreshold) }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="levelIcon" label="图标" width="80" align="center">
          <template slot-scope="scope">
            <el-image v-if="scope.row.levelIcon" :src="resolveUrl(scope.row.levelIcon)" style="width: 32px; height: 32px;" fit="contain"></el-image>
            <span v-else style="color: #c0c4cc;">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="levelColor" label="颜色" width="80" align="center">
          <template slot-scope="scope">
            <span v-if="scope.row.levelColor" :style="{display: 'inline-block', width: '24px', height: '24px', borderRadius: '4px', backgroundColor: scope.row.levelColor, verticalAlign: 'middle'}"></span>
            <span v-else style="color: #c0c4cc;">-</span>
          </template>
        </el-table-column>
        <el-table-column prop="levelOrder" label="排序" width="80" align="center"></el-table-column>
        <el-table-column prop="isActive" label="状态" width="80" align="center">
          <template slot-scope="scope">
            <el-tag :type="scope.row.isActive === 1 ? 'success' : 'info'" size="mini" effect="plain">{{ scope.row.isActive === 1 ? '启用' : '禁用' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="userCount" label="用户数" width="90" align="center">
          <template slot-scope="scope">
            <span style="font-weight: 600;">{{ scope.row.userCount || 0 }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="privilegeDescription" label="特权描述" min-width="200" show-overflow-tooltip></el-table-column>
        <el-table-column v-if="hasAnyActionPermission" :label="$t('common.operation')" :width="actionColumnWidth" fixed="right" align="center">
          <template slot-scope="scope">
            <div class="action-buttons">
              <el-button v-if="hasPermission('sys:level:update')" type="warning" size="mini" plain icon="el-icon-edit" @click="handleEdit(scope.row)">编辑</el-button>
              <el-button class="btn-action-logs" size="mini" plain icon="el-icon-document" @click="handleViewLogs(scope.row)">{{ $t('common.logs') }}</el-button>
              <el-button v-if="hasPermission('sys:level:delete')" type="danger" size="mini" plain icon="el-icon-delete" @click="handleDelete(scope.row)">删除</el-button>
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-container">
        <el-pagination
          :current-page="page"
          :page-size="limit"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @current-change="handleCurrentChange"
          @size-change="handleSizeChange"
        ></el-pagination>
      </div>
    </el-card>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogType === 'add' ? '新增等级' : '编辑等级'" :visible.sync="dialogVisible" width="600px" :close-on-click-modal="false">
      <el-form ref="levelForm" :model="formData" :rules="formRules" label-width="120px">
        <el-form-item label="等级标识" prop="levelCode">
          <el-input v-model="formData.levelCode" placeholder="如V0、V1、V2..." :disabled="dialogType === 'edit'" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="等级名称" prop="levelName">
          <el-input v-model="formData.levelName" placeholder="如普通用户、青铜会员..." maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="经验值阈值" prop="experienceThreshold">
          <el-input-number v-model="formData.experienceThreshold" :min="0" :max="9999999999" style="width: 100%"></el-input-number>
          <div style="font-size: 12px; color: #909399; margin-top: 4px;">达到此经验值即可升级</div>
        </el-form-item>
        <el-form-item label="排序序号" prop="levelOrder">
          <el-input-number v-model="formData.levelOrder" :min="0" :max="999" style="width: 100%"></el-input-number>
        </el-form-item>
        <el-form-item label="等级图标">
          <el-input v-model="formData.levelIcon" placeholder="图标URL"></el-input>
        </el-form-item>
        <el-form-item label="等级颜色">
          <el-color-picker v-model="formData.levelColor" show-alpha :predefine="predefineColors"></el-color-picker>
          <el-input v-model="formData.levelColor" placeholder="#RRGGBB" style="width: 150px; margin-left: 8px;" maxlength="20"></el-input>
        </el-form-item>
        <el-form-item label="特权描述">
          <el-input v-model="formData.privilegeDescription" type="textarea" :rows="3" placeholder="描述该等级的特权" maxlength="500"></el-input>
        </el-form-item>
        <el-form-item label="是否启用" prop="isActive">
          <el-switch v-model="formData.isActive" :active-value="1" :inactive-value="0" active-text="启用" inactive-text="禁用"></el-switch>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="formData.remark" type="textarea" :rows="2" maxlength="500"></el-input>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </div>
    </el-dialog>

    <!-- 经验值日志弹窗 -->
    <el-dialog title="经验值变动日志" :visible.sync="logDialogVisible" width="800px" append-to-body>
      <el-table :data="logData" v-loading="logLoading" style="max-height: 500px; overflow-y: auto;">
        <el-table-column prop="username" label="用户" width="120"></el-table-column>
        <el-table-column prop="experienceChange" label="变动量" width="100" align="center">
          <template slot-scope="scope">
            <span style="color: #67c23a; font-weight: 600;">+{{ scope.row.experienceChange }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="sourceType" label="来源" width="120">
          <template slot-scope="scope">
            <el-tag size="mini" effect="plain">{{ getSourceTypeLabel(scope.row.sourceType) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="experienceBefore" label="变动前" width="100" align="center"></el-table-column>
        <el-table-column prop="experienceAfter" label="变动后" width="100" align="center"></el-table-column>
        <el-table-column prop="operationTime" label="时间" width="160" align="center">
          <template slot-scope="scope">
            {{ formatDate(scope.row.operationTime) }}
          </template>
        </el-table-column>
        <el-table-column prop="remark" label="备注" min-width="150" show-overflow-tooltip></el-table-column>
      </el-table>
      <el-pagination
        v-if="logTotal > 0"
        style="margin-top: 12px; text-align: right;"
        :current-page="logPage"
        :page-size="logLimit"
        :total="logTotal"
        layout="total, prev, pager, next"
        @current-change="handleLogPageChange"
      ></el-pagination>
    </el-dialog>
  </div>
</template>

<script>
import { getLevelConfigPage, getLevelStatistics, createLevelConfig, updateLevelConfig, deleteLevelConfig, getExperienceLogPage } from '@/apis/system/level'
import { hasPermission } from '@/utils/permission'
import { getFullUrl } from '@/utils'

export default {
  name: 'LevelManagement',
  data() {
    return {
      loading: false,
      tableData: [],
      page: 1,
      limit: 10,
      total: 0,
      statistics: null,
      dialogVisible: false,
      dialogType: 'add',
      submitLoading: false,
      formData: {
        id: null,
        levelCode: '',
        levelName: '',
        experienceThreshold: 0,
        levelIcon: '',
        levelColor: '#909399',
        levelOrder: 0,
        privilegeDescription: '',
        isActive: 1,
        remark: ''
      },
      formRules: {
        levelCode: [{ required: true, message: '请输入等级标识', trigger: 'blur' }],
        levelName: [{ required: true, message: '请输入等级名称', trigger: 'blur' }],
        experienceThreshold: [{ required: true, message: '请输入经验值阈值', trigger: 'blur' }],
        levelOrder: [{ required: true, message: '请输入排序序号', trigger: 'blur' }],
        isActive: [{ required: true, message: '请选择启用状态', trigger: 'change' }]
      },
      logDialogVisible: false,
      logData: [],
      logLoading: false,
      logPage: 1,
      logLimit: 10,
      logTotal: 0,
      currentLevelCode: '',
      predefineColors: [
        '#909399', '#CD7F32', '#C0C0C0', '#FFD700', '#E5E4E2',
        '#B9F2FF', '#FF6B6B', '#9B59B6', '#E74C3C', '#F39C12', '#E91E63'
      ]
    }
  },
  created() {
    this.loadData()
    this.loadStatistics()
  },
  computed: {
    visibleActionCount() {
      let count = 0
      if (this.hasPermission('sys:level:update')) count += 1
      count += 1 // 日志按钮始终显示
      if (this.hasPermission('sys:level:delete')) count += 1
      return count
    },
    hasAnyActionPermission() {
      return this.visibleActionCount > 0
    },
    actionColumnWidth() {
      const count = this.visibleActionCount
      const widthMap = { 1: 100, 2: 170, 3: 245 }
      return widthMap[count] || 0
    }
  },
  methods: {
    hasPermission,
    resolveUrl(url) { return getFullUrl(url) },
    loadData() {
      this.loading = true
      getLevelConfigPage({ page: this.page, limit: this.limit }).then(res => {
        this.tableData = res?.list || []
        this.total = res?.total || 0
      }).catch(e => {
        console.error('加载等级列表失败:', e)
      }).finally(() => {
        this.loading = false
      })
    },
    loadStatistics() {
      getLevelStatistics().then(res => {
        this.statistics = res || null
      }).catch(() => {})
    },
    handleAdd() {
      this.dialogType = 'add'
      this.formData = { id: null, levelCode: '', levelName: '', experienceThreshold: 0, levelIcon: '', levelColor: '#909399', levelOrder: 0, privilegeDescription: '', isActive: 1, remark: '' }
      this.dialogVisible = true
      this.$nextTick(() => { this.$refs.levelForm && this.$refs.levelForm.clearValidate() })
    },
    handleEdit(row) {
      this.dialogType = 'edit'
      this.formData = { ...row }
      this.dialogVisible = true
      this.$nextTick(() => { this.$refs.levelForm && this.$refs.levelForm.clearValidate() })
    },
    handleSubmit() {
      this.$refs.levelForm.validate(valid => {
        if (!valid) return
        this.submitLoading = true
        const action = this.dialogType === 'add' ? createLevelConfig(this.formData) : updateLevelConfig(this.formData)
        action.then(() => {
          this.$message.success(this.dialogType === 'add' ? '创建成功' : '更新成功')
          this.dialogVisible = false
          this.loadData()
          this.loadStatistics()
        }).catch(e => {
          this.$message.error(e.message || '操作失败')
        }).finally(() => {
          this.submitLoading = false
        })
      })
    },
    handleDelete(row) {
      if (row.userCount > 0) {
        this.$message.warning(`等级[${row.levelName}]下存在${row.userCount}个用户，无法删除`)
        return
      }
      this.$confirm(`确定删除等级[${row.levelName}]吗？`, '提示', { type: 'warning' }).then(() => {
        deleteLevelConfig([row.id]).then(() => {
          this.$message.success('删除成功')
          this.loadData()
          this.loadStatistics()
        }).catch(e => {
          this.$message.error(e.message || '删除失败')
        })
      }).catch(() => {})
    },
    handleViewLogs(row) {
      this.currentLevelCode = row.levelCode
      this.logPage = 1
      this.logDialogVisible = true
      this.loadLogData()
    },
    loadLogData() {
      this.logLoading = true
      getExperienceLogPage({ page: this.logPage, limit: this.logLimit, sourceType: 'LEVEL_UP' }).then(res => {
        this.logData = res?.list || []
        this.logTotal = res?.total || 0
      }).catch(() => {}).finally(() => {
        this.logLoading = false
      })
    },
    handleLogPageChange(page) {
      this.logPage = page
      this.loadLogData()
    },
    handleSizeChange(size) {
      this.limit = size
      this.page = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.page = page
      this.loadData()
    },
    getSourceTypeLabel(type) {
      const map = { DEVICE_BIND: '设备绑定', POINTS_EARN: '积分获取', MANUAL: '手动调整', EXCHANGE_REFUND: '兑换退款' }
      return map[type] || type || '-'
    },
    formatNumber(num) {
      if (num === null || num === undefined) return '0'
      return num.toLocaleString()
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      const d = new Date(dateStr)
      const pad = n => String(n).padStart(2, '0')
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    }
  }
}
</script>

<style lang="scss" scoped>
@import "@/styles/list-page.scss";

.stats-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.stat-card {
  background: linear-gradient(145deg, #f0f9ff, #e0f2fe);
  border-radius: 12px;
  padding: 16px 24px;
  min-width: 120px;
  text-align: center;
  border: 1px solid rgba(14, 165, 233, 0.1);

  .stat-value {
    font-size: 24px;
    font-weight: 700;
    color: #0c4a6e;
  }

  .stat-label {
    font-size: 12px;
    color: #64748b;
    margin-top: 4px;
  }
}
</style>
